import { Field, useFormikContext } from 'formik'
import { Select } from 'formik-mui'
import * as mui from '@mui/material'
import {
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material'
import React from 'react'

import { langSwitchContext, Languages, translationsContext } from 'context/translations'
import { userContext } from 'context/user'
import * as schema from 'types/graphql/schema'
import { graphqlClient } from 'utils/graphql'
import { convertCurrency, convertCurrencyInputs } from 'utils/units'
import { renderValues, valuesToCalcInput, valuesType } from 'utils/values'

import { FieldSoB } from '../components/FieldSoB'
import { formatUnits as fu, formatValue as f } from '../utils'
import { TableSection } from '../components/TableSection'

import smartlineImage from './assets/smartline.png'


export function SectionInvestment(props: {
  calculate: (values: valuesType) => void
  calculationResult: schema.Calculate
  currencies: schema.Currency[]
  columnsStatuses: schema.ColumnStatus[]
  setColumnsStatuses: (columnsStatuses: schema.ColumnStatus[]) => void
  isPdf: boolean
  products: schema.Product[]
  productMetrics: schema.ProductMetrics
  standards: schema.Standards
}) {
  const t = React.useContext(translationsContext)
  const v = props.calculationResult

  const {values, setValues} = useFormikContext<valuesType>()
  const [lang, setLang] = React.useContext(langSwitchContext)
  const user = React.useContext(userContext)
  
  const [newTemplateName, setNewTemplateName] = React.useState(user.data?.defaultCalculationTemplate?.name || '')
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false)

  const disabledColumns = props.columnsStatuses.map(s => s !== schema.ColumnStatus.ON)

  if (user.spinner) return user.spinner

  function ColumnToggle({children, i}: {children: React.ReactNode, i: number}) {
    return <mui.Box sx={{'&:hover button': {opacity: 1}}}>
      <mui.IconButton
        disableRipple
        onClick={() => {
          const newColumnsStatuses = [...props.columnsStatuses]
          const currentValue = newColumnsStatuses[i]
          if (currentValue === schema.ColumnStatus.ON) {
            newColumnsStatuses[i] = schema.ColumnStatus.OFF
          } else {
            newColumnsStatuses[i] = schema.ColumnStatus.ON
          }
          props.setColumnsStatuses(newColumnsStatuses)
        }}
        size="small"
        sx={{
          '&:hover': {backgroundColor: 'none'},
          p: 0,
          opacity: disabledColumns[i] ? 1 : 0,
          ...(
            disabledColumns[i]
            ? {
                '&:hover .cancel': {display: 'none'},
                '&:hover .check': {display: 'block'},
              }
            : {}
          )
        }}
      >
        <CancelIcon className="cancel" sx={{opacity: .5}}/>
        <CheckIcon className="check" sx={{color: '#70B122', display: 'none'}}/>
      </mui.IconButton>
      <br/>
      {children}
    </mui.Box>
  }

  function disableColumnOnZero(value: string, i: number, fieldName: string) {
    if (value === '0') {
      const newColumnsStatuses = [...props.columnsStatuses]
      newColumnsStatuses[i] = schema.ColumnStatus.ZEROED
      props.setColumnsStatuses(newColumnsStatuses)
      return
    }
    return setValues({...values, [fieldName]: value})
  }

  return <mui.Box>
    <mui.Box sx={{alignItems: 'center', display: 'flex', flexDirection: 'row', mb: -.5}}>
      {/* Product */}
      <img src={smartlineImage} alt="smartline" height="45" style={{marginTop: '7px'}}/>
      <Field
        component={Select}
        name="productId"
        error={false}
        disabled={props.products.length <= 1}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const product = props.products.find(p => p.id === e.target.value)
          const productMetrics = product!.metrics
            .find(m => m.standards.id === props.standards.id) as schema.ProductMetrics

          const diseasePerformance = productMetrics.diseasePerformances.reduce((acc, d) => {
            acc[d.disease] = d
            return acc
          }, {} as {[d in schema.DiseasePerformanceDisease]: schema.DiseasePerformance })

          const newValues = renderValues(
            {
              ...values,
              productId: e.target.value,
              productPrice: convertCurrency(product!.price, 1, v.currency),
              investmentProductGramsPerCowDayCloseUp: productMetrics.productFeedingRates!.closeUp,
              investmentProductGramsPerCowDayFresh: productMetrics.productFeedingRates!.fresh,
              investmentProductGramsPerCowDayEarlyMidLact: productMetrics.productFeedingRates!.earlyMidLact,
              investmentProductGramsPerCowDayLateLact: productMetrics.productFeedingRates!.lateLact,

              shortTermReturnsMilkCurrent: productMetrics.milkPerformance.milk,
              shortTermReturnsMilkDifference: productMetrics.milkPerformanceDiff.milk,
              shortTermReturnsFatCurrent: productMetrics.milkPerformance.fat,
              shortTermReturnsFatDifference: productMetrics.milkPerformanceDiff.fat,
              shortTermReturnsProteinCurrent: productMetrics.milkPerformance.protein,
              shortTermReturnsProteinDifference: productMetrics.milkPerformanceDiff.protein,
              shortTermReturnsOtherSolidsCurrent: productMetrics.milkPerformance.otherSolids,
              shortTermReturnsOtherSolidsDifference: productMetrics.milkPerformanceDiff.otherSolids,

              longTermReturnsReproductionCostCurrent: productMetrics.economicPerformance.reproductionCost,
              longTermReturnsReplacementCostCurrent: productMetrics.economicPerformance.replacementCost,
              longTermReturnsCullValueCurrent: productMetrics.economicPerformance.cullValue,
              longTermReturnsCalfValueCurrent: productMetrics.economicPerformance.calfValue,
              longTermReturnsCullingRateCurrent: productMetrics.economicPerformance.cullingRate,

              longTermReturnsLastMonthToBreedCurrent: productMetrics.reproductivePerformance.lastMonthToBreed,
              longTermReturnsMinimumMilkToBreedCurrent: productMetrics.reproductivePerformance.minimumMilkToBreed,
              longTermReturnsPregnancyRateCurrent: productMetrics.reproductivePerformance.pregnancyRate,
              longTermReturnsPregnancyRateDifference: productMetrics.reproductivePerformance.pregnancyRateDiff,
              longTermReturnsPregnancyLossCurrent: productMetrics.reproductivePerformance.pregnancyLoss,
              longTermReturnsPregnancyLossDifference: productMetrics.reproductivePerformance.pregnancyLossDiff,

              longTermReturnsMastitisRegular: diseasePerformance.MASTITIS_CLINICAL.regularIncidence,
              longTermReturnsMastitisReduced: diseasePerformance.MASTITIS_CLINICAL.reducedIncidence,
              longTermReturnsKetosisClinicalRegular: diseasePerformance.KETOSIS_CLINICAL.regularIncidence,
              longTermReturnsKetosisClinicalReduced: diseasePerformance.KETOSIS_CLINICAL.reducedIncidence,
              longTermReturnsKetosisSubClinicalRegular: diseasePerformance.KETOSIS_SUBCLINICAL.regularIncidence,
              longTermReturnsKetosisSubClinicalReduced: diseasePerformance.KETOSIS_SUBCLINICAL.reducedIncidence,
              longTermReturnsMetritisRegular: diseasePerformance.METRITIS.regularIncidence,
              longTermReturnsMetritisReduced: diseasePerformance.METRITIS.reducedIncidence,
            },
            v.currency.code
          )
          setValues(newValues)
          props.calculate(newValues)
        }}
        sx={{
          '& fieldset': {border: 'none'},
          '& .MuiSelect-icon': { display: props.products.length <= 1 ? 'none' : undefined },
          '& .MuiSelect-select.Mui-disabled': { color: 'text.primary', WebkitTextFillColor: 'currentColor' },
        }}
      >
        {props.products.map(product => (
          <mui.MenuItem key={product.id} value={product.id}>
            <span dangerouslySetInnerHTML={{ __html: product.name }}/>
          </mui.MenuItem>
        ))}
      </Field>

      {/* Product price */}
      <mui.Stack direction="row" sx={{alignItems: 'center'}} gap={1}>
        <mui.Typography variant="body2" sx={{color: '#7F7F7F', ml: 3, verticalAlign: 'middle'}}>
          {v.currency.sign}
        </mui.Typography>
        <FieldSoB calculate={props.calculate} isPdf={props.isPdf} name="productPrice" sx={{backgroundColor: '#F2F2F2', borderRadius: '5px', width: '50px', px: '6px'}}/>
        <mui.Typography variant="body2" sx={{color: '#7F7F7F'}}>
          / {t('calc__price_units')}
        </mui.Typography>
      </mui.Stack>

      {/* Currency */}
      <Field
        component={Select}
        name="currencyId"
        error={false}
        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
          const oldCurrency = props.currencies.find(c => c.id === values.currencyId) as schema.Currency
          const newCurrency = props.currencies.find(c => c.id === e.target.value) as schema.Currency
          const newValues = await convertCurrencyInputs(
            props.products,
            (values: valuesType) => setValues(values, false),
            oldCurrency.conversionRate,
            newCurrency,
            values,
          )
          props.calculate(newValues)
        }}
        sx={{
          color: '#7F7F7F',
          fontSize: '1rem',
          '& .MuiSelect-select': {py: 0},
          '& fieldset': {border: 'none'},
        }}
      >
        {props.currencies.map(currency => (
          <mui.MenuItem key={currency.id} value={currency.id} sx={{fontSize: '1rem'}}>
            {currency.code}
          </mui.MenuItem>
        ))}
      </Field>

      {/* Language */}
      <Field
        component={Select}
        error={false}
        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
          setLang(e.target.value as schema.UserLanguages)
        }}
        sx={{
          color: '#7F7F7F',
          fontSize: '1rem',
          '& .MuiSelect-select': {py: 0},
          '& fieldset': {border: 'none'},
        }}
        value={lang}
      >
        {Object.entries(Languages).map(([key, label]) => (
          <mui.MenuItem key={key} value={key} sx={{fontSize: '1rem'}}>
            {label}
          </mui.MenuItem>
        ))}
      </Field>

      {/* Templates */}
      {!!user.data!.calculationTemplates.length &&
        <Field
          component={Select}
          displayEmpty
          error={false}
          onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value || null
            graphqlClient.chain.mutation
              .updateCurrentUser({
                defaultCalculationTemplateId: newValue === null ? null : parseInt(newValue),
              })
              .execute({__scalar: 1})
              .then(() => user.refetch())
          }}
          renderValue={(selected: string) => {
            if (!selected) {
              return <em>{t('calc__investment__field__default_template')}</em>
            }
            return user.data?.calculationTemplates.find(t => t.id === selected)!.name
          }}
          sx={{
            color: '#7F7F7F',
            fontSize: '1rem',
            '& .MuiSelect-select': {py: 0},
            '& fieldset': {border: 'none'},
          }}
          value={user.data!.defaultCalculationTemplate?.id || ""}
        >
          <mui.MenuItem value="" sx={{fontSize: '1rem'}}>
            <em>{t('calc__investment__field__default_template')}</em>
          </mui.MenuItem>
          {user.data?.calculationTemplates.map(template => (
            <mui.MenuItem key={template.id} value={template.id} sx={{display: 'flex', fontSize: '1rem'}}>
              {template.name}
              <mui.Box sx={{flexGrow: 1}}/>
              <mui.IconButton
                onClick={() => {
                  graphqlClient.chain.mutation
                    .templateDelete({id: template.id})
                    .execute({__scalar: 1})
                    .then(() => user.refetch())
                }}
                sx={{ml: 2}}
              >
                <DeleteIcon/>
              </mui.IconButton>
            </mui.MenuItem>
          ))}
        </Field>
      }
      <mui.IconButton onClick={() => setSaveDialogOpen(true)}>
        <SaveIcon/>
      </mui.IconButton>
    </mui.Box>

    {/* Save template dialog */}
    <mui.Dialog
      component="form"
      open={saveDialogOpen}
      onClose={() => setSaveDialogOpen(false)}
      onSubmit={e => {
        e.preventDefault()
        graphqlClient.chain.mutation
          .templateSave({
            name: newTemplateName,
            calculationInput: valuesToCalcInput(values, props.columnsStatuses),
          })
          .execute({__scalar: 1})
          .then(() => user.refetch())
      }}
      maxWidth="sm"
      fullWidth
    >
      <mui.DialogContent>
        <mui.TextField
          margin="dense"
          label={t('calc__investment__field__template_name')}
          fullWidth
          variant="standard"
          value={newTemplateName}
          onChange={(v) => setNewTemplateName(v.target.value.slice(0, 40))}
        />
      </mui.DialogContent>

      <mui.DialogActions sx={{p: 3, pt: 1}}>
        <mui.Button color="error" onClick={() => setSaveDialogOpen(false)}>{t('calc__investment__button__cancel')}</mui.Button>
        <mui.Button
          color="success"
          variant="contained"
          type="submit"
        >
          {t('calc__investment__button__save_template')}
        </mui.Button>
      </mui.DialogActions>
    </mui.Dialog>

    <TableSection
      colorGroupText="white"
      colorGroupBg="#00A3C4"
      colorTableBg="#E1FAFF"
      colorResultBg="#00A3C4"
      columns={[[
        '',
        {content: <mui.Box sx={{verticalAlign: 'bottom'}}>{t('values__units')}</mui.Box>},
        {content: <ColumnToggle i={0}>{t('calc__investment__column_2')}</ColumnToggle>, tooltip: t('calc__investment__column_2_tt')},
        {content: <ColumnToggle i={1}>{t('calc__investment__column_3')}</ColumnToggle>, tooltip: t('calc__investment__column_3_tt')},
        {content: <ColumnToggle i={2}>{t('calc__investment__column_4')}</ColumnToggle>, tooltip: t('calc__investment__column_4_tt')},
        {content: <ColumnToggle i={3}>{t('calc__investment__column_5')}</ColumnToggle>, tooltip: t('calc__investment__column_5_tt')},
        {content: <ColumnToggle i={4}>{t('calc__investment__column_6')}</ColumnToggle>, tooltip: t('calc__investment__column_6_tt')},
      ]]}
      data={[
        [
          {
            content: t('calc__investment__1_1'),
            cellProps: {rowSpan: 2},
            borderBottom: true,
          },
          {
            ...fu(`%/${t('units__phase')}`),
            tooltip: t('calc__investment__1_2__tt')
          },
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[0]} isPdf={props.isPdf} name="investmentHerdStructureFarOff" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[1]} isPdf={props.isPdf} name="investmentHerdStructureCloseUp" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[2]} isPdf={props.isPdf} name="investmentHerdStructureFresh" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[3]} isPdf={props.isPdf} name="investmentHerdStructureEarlyMidLact" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[4]} isPdf={props.isPdf} name="investmentHerdStructureLateLact" />},
        ],
        [
          {
            ...fu(`${t('units__cow')} #/${t('units__phase')}`),
            borderBottom: true,
            tooltip:t('calc__investment__2_2__tt')
          },
          {...f(v.investment.cowsAmount.farOff, 0, 1, disabledColumns[0]), borderBottom: true},
          {...f(v.investment.cowsAmount.closeUp, 0, 1, disabledColumns[1]), borderBottom: true},
          {...f(v.investment.cowsAmount.fresh, 0, 1, disabledColumns[2]), borderBottom: true},
          {...f(v.investment.cowsAmount.earlyMidLact, 0, 1, disabledColumns[3]), borderBottom: true},
          {...f(v.investment.cowsAmount.lateLact, 0, 1, disabledColumns[4]), borderBottom: true},
        ],
        [
          {content: v.product.name.replace(/®/g, ''), cellProps: {rowSpan: 2}, borderBottom: true},
          {
            ...fu(`${t('units__g')}/${t('units__cow')}/${t('units__day')}`),
            tooltip: t('calc__investment__3_2__tt')
          },
          f(null, 2, 1, disabledColumns[0]),
          props.columnsStatuses[1] === schema.ColumnStatus.ON
            ? {content: <FieldSoB
                calculate={props.calculate}
                disabled={disabledColumns[1]}
                isPdf={props.isPdf}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableColumnOnZero(e.target.value, 1, 'investmentProductGramsPerCowDayCloseUp')}
                value={values['investmentProductGramsPerCowDayCloseUp']}
                name="investmentProductGramsPerCowDayCloseUp"
              />}
            : {content: <FieldSoB
                calculate={props.calculate}
                disabled
                isPdf={props.isPdf}
                name='dummy'
                value={props.columnsStatuses[1] === schema.ColumnStatus.ZEROED ? 0 : values.investmentProductGramsPerCowDayCloseUp}
              />},
          props.columnsStatuses[2] === schema.ColumnStatus.ON
            ? {content: <FieldSoB
                calculate={props.calculate}
                disabled={disabledColumns[2]}
                isPdf={props.isPdf}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableColumnOnZero(e.target.value, 2, 'investmentProductGramsPerCowDayFresh')}
                value={values['investmentProductGramsPerCowDayFresh']}
                name="investmentProductGramsPerCowDayFresh"
              />}
            : {content: <FieldSoB
                calculate={props.calculate}
                disabled
                isPdf={props.isPdf}
                name='dummy'
                value={props.columnsStatuses[2] === schema.ColumnStatus.ZEROED ? 0 : values.investmentProductGramsPerCowDayFresh}
              />},
          props.columnsStatuses[3] === schema.ColumnStatus.ON
            ? {content: <FieldSoB
                calculate={props.calculate}
                disabled={disabledColumns[3]}
                isPdf={props.isPdf}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableColumnOnZero(e.target.value, 3, 'investmentProductGramsPerCowDayEarlyMidLact')}
                value={values['investmentProductGramsPerCowDayEarlyMidLact']}
                name="investmentProductGramsPerCowDayEarlyMidLact"
              />}
            : {content: <FieldSoB
                calculate={props.calculate}
                disabled
                isPdf={props.isPdf}
                name='dummy'
                value={props.columnsStatuses[3] === schema.ColumnStatus.ZEROED ? 0 : values.investmentProductGramsPerCowDayEarlyMidLact}
              />},
          props.columnsStatuses[4] === schema.ColumnStatus.ON
            ? {content: <FieldSoB
                calculate={props.calculate}
                disabled={disabledColumns[4]}
                isPdf={props.isPdf}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableColumnOnZero(e.target.value, 4, 'investmentProductGramsPerCowDayLateLact')}
                value={values['investmentProductGramsPerCowDayLateLact']}
                name="investmentProductGramsPerCowDayLateLact"
              />}
            : {content: <FieldSoB
                calculate={props.calculate}
                disabled
                isPdf={props.isPdf}
                name='dummy'
                value={props.columnsStatuses[4] === schema.ColumnStatus.ZEROED ? 0 : values.investmentProductGramsPerCowDayLateLact}
              />},
        ],
        [
          {...fu(`${v.currency.sign}/${t('units__cow')}/${t('units__day')}`), borderBottom: true},
          {...f(v.investment.productPricePerCowDay.farOff, 3, 1, disabledColumns[0]), borderBottom: true},
          {...f(v.investment.productPricePerCowDay.closeUp, 3, 1, disabledColumns[1]), borderBottom: true},
          {...f(v.investment.productPricePerCowDay.fresh, 3, 1, disabledColumns[2]), borderBottom: true},
          {...f(v.investment.productPricePerCowDay.earlyMidLact, 3, 1, disabledColumns[3]), borderBottom: true},
          {...f(v.investment.productPricePerCowDay.lateLact, 3, 1, disabledColumns[4]), borderBottom: true},
        ],
        [
          {
            content: t('calc__investment__4_1'),
            tooltip: t('calc__investment__4_1__tt')
          },
          fu(`${t('units__'+v.weightUnits)}/${t('units__cow')}/${t('units__day')}`),
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[0]} isPdf={props.isPdf} name="investmentDmiFarOff" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[1]} isPdf={props.isPdf} name="investmentDmiCloseUp" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[2]} isPdf={props.isPdf} name="investmentDmiFresh" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[3]} isPdf={props.isPdf} name="investmentDmiEarlyMidLact" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[4]} isPdf={props.isPdf} name="investmentDmiLateLact" />},
        ],
        [
          {
            content: <i> + <span dangerouslySetInnerHTML={{ __html: v.product.name.replace(/®/g, '') }}/></i>,
            tooltip: t('calc__investment__5_1__tt'),
            borderBottom: true,
          },
          {...fu(`${t('units__'+v.weightUnits)}/${t('units__cow')}/${t('units__day')}`), borderBottom: true},
          {
            ...f(v.investment.dmiWithProduct.farOff ? `+${v.investment.dmiWithProduct.farOff}` : null, 2, 1, disabledColumns[0]),
            cellProps: {sx: (v.investment.dmiWithProduct.farOff ? {color: '#EC671F', '& span': {fontWeight: 700}} : {})},
            borderBottom: true,
          },
          {
            ...f(v.investment.dmiWithProduct.closeUp ? `+${v.investment.dmiWithProduct.closeUp}` : null, 2, 1, disabledColumns[1]),
            cellProps: {sx: (v.investment.dmiWithProduct.closeUp ? {color: '#EC671F', '& span': {fontWeight: 700}} : {})},
            borderBottom: true,
          },
          {
            ...f(v.investment.dmiWithProduct.fresh ? `+${v.investment.dmiWithProduct.fresh}` : null, 2, 1, disabledColumns[2]),
            cellProps: {sx: (v.investment.dmiWithProduct.fresh ? {color: '#EC671F', '& span': {fontWeight: 700}} : {})},
            borderBottom: true,
          },
          {
            ...f(v.investment.dmiWithProduct.earlyMidLact ? `+${v.investment.dmiWithProduct.earlyMidLact}` : null, 2, 1, disabledColumns[3]),
            cellProps: {sx: {color: v.investment.dmiWithProduct.earlyMidLact ? 'darkred' : undefined}},
            borderBottom: true,
          },
          {
            ...f(v.investment.dmiWithProduct.lateLact ? `+${v.investment.dmiWithProduct.lateLact}` : null, 2, 1, disabledColumns[4]),
            cellProps: {sx: {color: v.investment.dmiWithProduct.lateLact ? 'darkred' : undefined}},
            borderBottom: true,
          },
        ],
        [
          {
            content: t('calc__investment__6_1'),
            tooltip: t('calc__investment__6_1__tt')
          },
          fu(`${v.currency.sign}/${t('units__'+v.weightUnits)} ${t('units__dm')}`),
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[0]} isPdf={props.isPdf} name="investmentDmCostCurrentFarOff" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[1]} isPdf={props.isPdf} name="investmentDmCostCurrentCloseUp" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[2]} isPdf={props.isPdf} name="investmentDmCostCurrentFresh" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[3]} isPdf={props.isPdf} name="investmentDmCostCurrentEarlyMidLact" />},
          {content: <FieldSoB calculate={props.calculate} disabled={disabledColumns[4]} isPdf={props.isPdf} name="investmentDmCostCurrentLateLact" />},
        ],
        [
          {
            content: <i> + <span dangerouslySetInnerHTML={{__html: v.product.name.replace(/®/g, '')}}/></i>,
            // tooltip: t('calc__investment__7_1__tt'),
            borderBottom: true,
          },
          {...fu(`${v.currency.sign}/${t('units__'+v.weightUnits)} ${t('units__dm')}`), borderBottom: true},
          {...f(v.investment.dmCostWithProduct.farOff, 3, 1, disabledColumns[0]), borderBottom: true},
          {...f(v.investment.dmCostWithProduct.closeUp, 3, 1, disabledColumns[1]), borderBottom: true},
          {...f(v.investment.dmCostWithProduct.fresh, 3, 1, disabledColumns[2]), borderBottom: true},
          {...f(v.investment.dmCostWithProduct.earlyMidLact, 3, 1, disabledColumns[3]), borderBottom: true},
          {...f(v.investment.dmCostWithProduct.lateLact, 3, 1, disabledColumns[4]), borderBottom: true},
        ],
        [
          {
            content: t('calc__investment__8_1'),
            // tooltip: t('calc__investment__8_1__tt'),
          },
          fu(`${v.currency.sign}/${t('units__cow')}/${t('units__day')}`),
          f(v.investment.feedCostPerCowDay.farOff, 2, 1, disabledColumns[0]),
          f(v.investment.feedCostPerCowDay.closeUp, 2, 1, disabledColumns[1]),
          f(v.investment.feedCostPerCowDay.fresh, 2, 1, disabledColumns[2]),
          f(v.investment.feedCostPerCowDay.earlyMidLact, 2, 1, disabledColumns[3]),
          f(v.investment.feedCostPerCowDay.lateLact, 2, 1, disabledColumns[4]),
        ],
        [
          {
            content: <i> + <span dangerouslySetInnerHTML={{__html: v.product.name.replace(/®/g, '')}}/></i>,
            // tooltip: t('calc__investment__9_1__tt'),
          },
          fu(`${v.currency.sign}/${t('units__cow')}/${t('units__day')}`),
          f(v.investment.feedCostPerCowDayWithProduct.farOff, 2, 1, disabledColumns[0]),
          f(v.investment.feedCostPerCowDayWithProduct.closeUp, 2, 1, disabledColumns[1]),
          {
            ...f(v.investment.feedCostPerCowDayWithProduct.fresh, 2, 1, disabledColumns[2]),
            cellProps: {sx: (values.aabCents ? {color: 'green', '& span': {fontWeight: 700}} : {})}
          }, {
            ...f(v.investment.feedCostPerCowDayWithProduct.earlyMidLact, 2, 1, disabledColumns[3]),
            cellProps: {sx: (values.aabCents ? {color: 'green', '& span': {fontWeight: 700}} : {})}
          }, {
            ...f(v.investment.feedCostPerCowDayWithProduct.lateLact, 2, 1, disabledColumns[4]),
            cellProps: {sx: (values.aabCents ? {color: 'green', '& span': {fontWeight: 700}} : {})}
          },
        ],
        [
          {content: t('calc__investment__8_1'), borderTop: true},
          {...fu(`${v.currency.sign}/${t('units__cow')}/${t('units__year')}`), borderTop: true},
          {
            content: f(v.investment.totalFeedCostPerCowYear, 0).content,
            cellProps: {colSpan: 3},
            borderTop: true,
          }, {
            ...(f(`${f(v.investment.totalFeedCostPerCowYearDifference, 2).text} ${t('values__difference')}`)),
            cellProps: {colSpan: 2, rowSpan: 2},
            borderTop: true,
          },
        ],
        [
          {content: <i> + <span dangerouslySetInnerHTML={{__html: v.product.name.replace(/®/g, '')}}/></i>},
          fu(`${v.currency.sign}/${t('units__cow')}/${t('units__year')}`),
          {
            content: f(v.investment.totalFeedCostPerCowYearWithProduct, 0).content,
            cellProps: {colSpan: 3}
          },
        ],
      ]}
      dataRowsCollapsible={[0, 1, 4, 5, 6, 7, 10, 11]}
      groups={[
        {
          content: t('calc__investment__title'),
          firstRow: 0,
        },
      ]}
      isPdf={props.isPdf}
      result={{
        padding: '0px',
        title: <mui.Box sx={{verticalAlign: 'bottom'}}>{t('calc__investment__result_header')}</mui.Box>,
        subtitle: t('calc__investment__result'),
        units: `${v.currency.sign}/${t('units__' + values.resultsUnitsAmount)}/${t('units__' + values.resultsUnitsPeriod)}`,
        value: f(v.investment.totalFeedCostDifference, 2).text,
      }}
    />
  </mui.Box>
}